/**
 * PhotoIntakeDialog — the "Add from a photo" flow (photo_intake tool).
 *
 * A care manager photographs a paper medication list, a provider
 * contact list or a provider's business card (LP-32: one card, one
 * provider row with its name, role, organisation, phone, email and
 * address); the model transcribes the legible rows; the CM confirms
 * each one. Nothing enters the record until a row is confirmed, and each
 * confirmed row rides the SAME record-add path the manual "+ Add" uses
 * (postRecordRow) — the auto-join + dedupe safety work happens there for
 * free. Confirm-everything: clinical data arriving by camera, nothing
 * lands without a human tap.
 *
 * Three beats:
 *   1. Choose a photo (on a phone, the camera or the photo library).
 *   2. The reading beat — the shared ReadingIndicator, front and center.
 *   3. Review — each transcribed row is the confirm queue's card: a kind
 *      chip, Adjust at the top, its facts on labelled lines in the record
 *      form's words, then "Add medication" / "Add to care team" first and
 *      Dismiss. "Add {n} to the record" above the list adds every ready
 *      card, one at a time in list order. A medication with no name says
 *      so and offers no add. Adjust is the record form's controls: the
 *      When ticks, the care-team picker for the prescriber, a phone that
 *      formats as typed, an address box that grows. A provider can be
 *      linked to its Directory profile before it is added. These rows
 *      carry no proposal id and ride the record-add path, so the panel is
 *      its own, drawn in the queue's design.
 *
 * LP-45: the review arrives with each row checked against the client's
 * record by the server (record_match, matchPhotoRowsToRecord), with the
 * record's own rules. A twin of an active medication, or a provider
 * already on the care team, reads "Already on the record" and comes
 * unticked; a medication of the same name with another dose or schedule,
 * or a look-alike name, asks "Did you mean {X}, already on the record?"
 * with "Update {X}" (an edit of that row) and "Keep both" (the add), the
 * confirm queue's words and actions; everything else reads "New", ticked.
 * The state is a tag beside the chip; the row's box says what it does
 * ("Add {name}", "Add {name} anyway") and is how a checked row is added:
 * "Add {n} to the record", above the list and beside Done, adds the
 * ticked rows only. A row that asks waits for its answer. A read without
 * a match (no client, or a seat that may not open the record) reads as
 * before: no tag, no box, each row its own Add.
 *
 * LP-45: several photos at once. The picker takes more than one; the
 * photos are read one after another, in the order picked, each its own
 * call to the server (one image per call, the body cap and the ephemeral
 * handling unchanged), into one review list. When more than one photo was
 * read, each row carries a small "Photo {n}" mark, and the reading line
 * counts ("Reading photo 2 of 5…"). A photo that fails shows one line,
 * "Photo {n} couldn't be read. Take a clearer photo in good light.", and
 * the rest go on; when every photo fails the pick beat's notice shows, as
 * for one photo. No progress bars, background reads, retries or
 * reordering.
 *
 * The image is EPHEMERAL — downscaled in the browser, read once, never
 * saved (see photoIntakeApi.ts / the handler header).
 *
 * House copy: no engine words (no "OCR", "extract", "model"); warm,
 * plain, second person. The image is never named to the record.
 */

import { useSupportContact } from '@/hooks/useSupportContact';
import { type ReactNode, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Camera, Check } from 'lucide-react';
import { Dialog } from '@/components/primitives/Dialog';
import { Button } from '@/components/primitives/Button';
import { Field } from '@/components/primitives/Field';
import { TextInput } from '@/components/primitives/TextInput';
import { TickGroup } from '@/components/primitives/TickGroup';
import { FLD } from '@/components/primitives/modalFormStyles';
import { ErrorNotice } from '@/components/ErrorNotice';
import { ReadingIndicator } from '@/components/notes/ReadingIndicator';
import { CareTeamCombobox, type CareTeamPick } from '@/components/clientRecord/CareTeamCombobox';
import { DirectorySearch } from '@/components/clientRecord/AddProviderDialog';
import { photoIntakeKeyOf } from '@/lib/errorCopy';
import { formatPhoneDisplay, formatPhoneInput, normalizePhone } from '@/lib/phone';
import { cachedClientRecord } from '@/lib/clientRecordApi';
import { RECORD_VALUE_LABELS, careTeamFieldOf } from '@/lib/recordFormWords';
import { cardFieldLabel, medicationRowValue } from '@/lib/confirmQueueActions';
import { CheckboxRow } from '@/components/primitives/CheckboxRow';
import { factLabelOverride } from '@/lib/fieldLabels';
import { MEDICATION_TIMES_OF_DAY } from '@/lib/noteVocabulary';
import {
  downscaleImageForIntake,
  extractPhotoIntake,
  type PhotoIntakeMedicationRow,
  type PhotoIntakeProviderRow,
  type PhotoRecordMedication,
} from '@/lib/photoIntakeApi';
import {
  RECORD_ADD_PROJECTIONS,
  patchRecordRow,
  postRecordRow,
  recordEditErrorMessage,
  type RecordAddProjection,
  type RecordEditErrorContext,
} from '@/lib/clientRecordEditApi';
import { PROPOSAL_PAYLOAD_SPECS, type TextKind } from '@/lib/noteVocabulary';
import { textBoxProps } from '@/lib/textBoxProps';
import { displayValue } from '@/lib/valueCase';
import { AddressFields } from '@/components/clientRecord/AddressesCard';
import { suggestAddressParts } from '@/lib/addressSuggestion';
import { formatAddress } from '@/lib/address';

/** A provider card's office address parts, kept as `office_address.<part>`
 *  in the card's fields (0234). */
const ADDRESS_PARTS = ['line1', 'line2', 'city', 'state', 'zip'] as const;
type AddressPart = (typeof ADDRESS_PARTS)[number];
const partKey = (p: AddressPart): string => `office_address.${p}`;
/** What the photo read as one line, shown above the fields. */
const PHOTO_TEXT_KEY = 'office_address.photo_text';

// ---------------------------------------------------------------------
// Per-row draft state (the review beat). A card is pending until it is
// added (adding, then added) or dismissed (removed).
// ---------------------------------------------------------------------

type RowKind = 'medication' | 'provider';

interface DirectoryLink {
  kind: 'org' | 'individual';
  id: string;
  name: string;
}

interface DraftRow {
  id: string;
  kind: RowKind;
  /** Which photo it was read from, counting from 1 in the order picked. */
  photo: number;
  /** Editable text values keyed by the record-add field name. */
  fields: Record<string, string>;
  /** A medication's When ticks, in the vocabulary's order. */
  times: string[];
  /** A prescriber picked from the care team or the Directory. */
  prescriberPick: CareTeamPick | null;
  /** A provider linked to its Directory profile before it is added. */
  directoryLink: DirectoryLink | null;
  /** A provider's Directory search is open. */
  searching: boolean;
  status: 'pending' | 'adding' | 'added';
  /** What the press did: a new row, or an edit of one on the record. */
  outcome: 'added' | 'updated';
  adjusting: boolean;
  error: string | null;
  /** The row against the client's record, the server's word; null when
   *  the read carried none (LP-45). */
  match: RowMatch | null;
  /** "Add {n} to the record" takes this row. */
  ticked: boolean;
}

type RowMatch =
  | { state: 'on_record' }
  | { state: 'near'; rows: PhotoRecordMedication[] }
  | { state: 'new' };

function rowMatchOf(m: PhotoIntakeMedicationRow['record_match'] | PhotoIntakeProviderRow['record_match']): RowMatch | null {
  if (m === undefined) return null;
  if (m.state === 'near') return m.rows.length > 0 ? { state: 'near', rows: m.rows } : { state: 'new' };
  return { state: m.state };
}

let seq = 0;
const nextId = (): string => `intake-row-${seq++}`;

/** The fields per kind, in the record form's order. */
const MED_KEYS = ['name', 'dose', 'times_of_day', 'schedule', 'prescriber'] as const;
const PROVIDER_KEYS = ['display_name', 'role', 'office_phone', 'email', 'office_address'] as const;
const PROJECTION: Record<RowKind, RecordAddProjection> = { medication: 'medications', provider: 'providers' };
const CHIP: Record<RowKind, string> = { medication: 'Medication', provider: 'Care provider' };
const ADD_WORDS: Record<RowKind, string> = { medication: 'Add medication', provider: 'Add to care team' };
const PRESCRIBER = careTeamFieldOf('medications', 'prescriber')!;
const WHEN_WORDS = RECORD_VALUE_LABELS.medications?.times_of_day ?? {};

function keysFor(kind: RowKind): readonly string[] {
  return kind === 'medication' ? MED_KEYS : PROVIDER_KEYS;
}

/** A field's words as the confirm queue reads them (a new provider's
 *  display name reads "Name"), which are the record form's elsewhere. */
function labelFor(kind: RowKind, key: string): string {
  const type = kind === 'medication' ? 'medication_add' : 'provider_new';
  return factLabelOverride(type, key) ?? cardFieldLabel(type, key);
}

/** The registry's text kind for a row's field: how its box behaves and
 *  how its value reads. */
function textKindFor(kind: RowKind, key: string): TextKind | undefined {
  const spec = kind === 'medication' ? PROPOSAL_PAYLOAD_SPECS.medication_add : PROPOSAL_PAYLOAD_SPECS.provider_new;
  return spec[key]?.textKind;
}

/** A card's name: the first field, trimmed. */
function nameOf(row: DraftRow): string {
  return (row.fields[keysFor(row.kind)[0]!] ?? '').trim();
}

/** A card that may be added now: named, not added, not in flight. */
function isReady(row: DraftRow): boolean {
  return row.status === 'pending' && nameOf(row) !== '';
}

function medToDraft(row: PhotoIntakeMedicationRow, photo: number): DraftRow {
  const read = new Set(row.times_of_day ?? []);
  return {
    id: nextId(),
    kind: 'medication',
    photo,
    fields: { name: row.name ?? '', dose: row.dose ?? '', schedule: row.schedule ?? '', prescriber: row.prescriber ?? '' },
    times: MEDICATION_TIMES_OF_DAY.filter((t) => read.has(t)),
    prescriberPick: null,
    directoryLink: null,
    searching: false,
    status: 'pending',
    outcome: 'added',
    adjusting: false,
    error: null,
    ...matchFields(rowMatchOf(row.record_match)),
  };
}

/** A twin comes unticked; a row that asks is answered on its card. */
function matchFields(match: RowMatch | null): { match: RowMatch | null; ticked: boolean } {
  return { match, ticked: match === null || match.state === 'new' };
}

function providerToDraft(row: PhotoIntakeProviderRow, photo: number): DraftRow {
  // The office address as its five parts. A photo read as one line keeps
  // its words above the fields, which open on what the house parser reads
  // from them; only the fields travel.
  const read = row.office_address;
  const text = typeof read === 'string' && read.trim() !== '' ? read.trim() : null;
  const parts = read !== null && typeof read === 'object' ? read : text !== null ? suggestAddressParts(text, 'street') : null;
  const address: Record<string, string> = {};
  for (const p of ADDRESS_PARTS) address[partKey(p)] = parts?.[p] ?? '';
  if (text !== null) address[PHOTO_TEXT_KEY] = text;
  return {
    id: nextId(),
    kind: 'provider',
    photo,
    fields: {
      display_name: row.display_name ?? '',
      role: row.role ?? '',
      office_phone: row.office_phone ? formatPhoneDisplay(row.office_phone) : '',
      email: row.email ?? '',
      ...address,
    },
    times: [],
    prescriberPick: null,
    directoryLink: null,
    searching: false,
    status: 'pending',
    outcome: 'added',
    adjusting: false,
    error: null,
    ...matchFields(rowMatchOf(row.record_match)),
  };
}

/** "Add {n} to the record" takes a ready row that is ticked; a row that
 *  asks "Did you mean" waits for its own answer. */
function inBulk(row: DraftRow): boolean {
  return isReady(row) && row.ticked && row.match?.state !== 'near';
}

/**
 * The edit "Update {X}" sends to the row on the record: the photo's dose,
 * directions and When, as the record form's edit sends a change. The
 * name stays the record's (a look-alike is a spelling, never a rename);
 * the prescriber words ride as the form's edit carries them, the photo's
 * when it read one, else the record's.
 */
function updateFields(row: DraftRow, recordPrescriber: string | undefined): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of ['dose', 'schedule'] as const) {
    const v = (row.fields[key] ?? '').trim();
    if (v !== '') out[key] = v;
  }
  if (row.times.length > 0) out['times_of_day'] = [...row.times];
  const prescriber = (row.fields['prescriber'] ?? '').trim();
  if (prescriber !== '') out['prescriber'] = prescriber;
  else if (recordPrescriber !== undefined && recordPrescriber.trim() !== '') out['prescriber'] = recordPrescriber.trim();
  return out;
}

/**
 * The record-add `fields` an added card sends, as the record form sends
 * them: non-empty values only, a phone normalized, the When ticks only
 * when ticked, a prescriber pick as exactly one id field, a provider's
 * Directory link as one id. Specialty and organization never travel.
 */
function addFields(row: DraftRow): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of keysFor(row.kind)) {
    if (key === 'times_of_day') {
      if (row.times.length > 0) out[key] = [...row.times];
      continue;
    }
    if (key === 'office_address') {
      const parts = addressPartsOf(row);
      if (parts !== null) out[key] = parts;
      continue;
    }
    const v = (row.fields[key] ?? '').trim();
    if (v.length === 0) continue;
    out[key] = key === 'office_phone' ? normalizePhone(v) : v;
  }
  const pick = row.prescriberPick;
  if (row.kind === 'medication' && pick !== null) {
    if (pick.kind === 'care_team') {
      if (PRESCRIBER.providerIdField !== undefined) out[PRESCRIBER.providerIdField] = pick.providerId;
    } else if (pick.type === 'individual') {
      if (PRESCRIBER.individualField !== undefined) out[PRESCRIBER.individualField] = pick.id;
    } else {
      out[PRESCRIBER.orgField] = pick.id;
    }
  }
  if (row.kind === 'provider' && row.directoryLink !== null) {
    out[row.directoryLink.kind === 'org' ? 'directory_org_id' : 'directory_individual_id'] = row.directoryLink.id;
  }
  return out;
}

/** A provider card's office address as the record add takes it: the
 *  five parts, blanks as null; null when no part is filled. */
function addressPartsOf(row: DraftRow): Record<AddressPart, string | null> | null {
  const out = {} as Record<AddressPart, string | null>;
  for (const p of ADDRESS_PARTS) {
    const v = (row.fields[partKey(p)] ?? '').trim();
    out[p] = v === '' ? null : v;
  }
  return ADDRESS_PARTS.some((p) => out[p] !== null) ? out : null;
}

/** The card's facts: each field with a value, in the record form's words. */
function factsOf(row: DraftRow): Array<{ key: string; label: string; value: string }> {
  const out: Array<{ key: string; label: string; value: string }> = [];
  for (const key of keysFor(row.kind)) {
    let value: string;
    if (key === 'times_of_day') value = row.times.map((t) => WHEN_WORDS[t] ?? t).join(', ');
    else if (key === 'office_phone') value = formatPhoneDisplay((row.fields[key] ?? '').trim());
    else if (key === 'office_address') {
      const parts = addressPartsOf(row);
      value = parts === null ? '' : formatAddress({ line1: parts.line1 ?? '', line2: parts.line2 ?? undefined, city: parts.city ?? undefined, state: parts.state ?? undefined, zip: parts.zip ?? undefined });
    } else {
      const raw = (row.fields[key] ?? '').trim();
      value = textKindFor(row.kind, key) === 'medication' ? displayValue(raw, 'medication') : raw;
    }
    if (value.trim() !== '') out.push({ key, label: labelFor(row.kind, key), value });
  }
  return out;
}

/** A refused add reads in the row's own words: its fields' registry
 *  specs, the values the row held, and the labels its boxes show. */
function errorContextFor(row: DraftRow): RecordEditErrorContext {
  const labels: Record<string, string> = {};
  for (const key of keysFor(row.kind)) labels[key] = labelFor(row.kind, key);
  return {
    specs: PROPOSAL_PAYLOAD_SPECS[RECORD_ADD_PROJECTIONS[PROJECTION[row.kind]].specType],
    sent: row.fields,
    labels,
  };
}

// ---------------------------------------------------------------------
// The dialog
// ---------------------------------------------------------------------

export interface PhotoIntakeDialogProps {
  onClose: () => void;
}

type Phase = 'pick' | 'reading' | 'review';

/** One reading of the picked photos: every row, which photos failed, how
 *  many were picked. */
interface PhotosRead {
  rows: DraftRow[];
  failed: number[];
  total: number;
}

/** The line a photo that could not be read shows in the review. */
function photoFailedLine(n: number): string {
  return `Photo ${n} couldn't be read. Take a clearer photo in good light.`;
}

export function PhotoIntakeDialog({ onClose }: PhotoIntakeDialogProps): ReactNode {
  const { tenantSlug = '', clientId = '' } = useParams<{ tenantSlug: string; clientId: string }>();
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  // The error copy's contact, from the shell's cached tenant config.
  const contact = useSupportContact({ enabled: false });

  const [phase, setPhase] = useState<Phase>('pick');
  const [rows, setRows] = useState<DraftRow[]>([]);
  // Did the photo yield ANY rows? Distinguishes "no list found" from
  // "the CM dismissed everything" once the list empties out.
  const [foundRows, setFoundRows] = useState(false);
  const [extractErrorKey, setExtractErrorKey] = useState<string | null>(null);
  // Which photo of how many is being read, while the reading beat shows.
  const [readingPhoto, setReadingPhoto] = useState<{ done: number; total: number } | null>(null);
  // The photos that could not be read, and how many were picked.
  const [failedPhotos, setFailedPhotos] = useState<number[]>([]);
  const [photoTotal, setPhotoTotal] = useState(1);

  const extractMut = useMutation<PhotosRead, unknown, File[]>({
    // One photo at a time, in the order picked; a photo that fails is
    // noted and the rest go on. Every photo failing is the one failure.
    mutationFn: async (files: File[]) => {
      const out: PhotosRead = { rows: [], failed: [], total: files.length };
      let firstError: unknown = null;
      for (const [i, file] of files.entries()) {
        const photo = i + 1;
        setReadingPhoto({ done: photo, total: files.length });
        try {
          const image = await downscaleImageForIntake(file);
          const data = await extractPhotoIntake(tenantSlug, { image, clientId });
          out.rows.push(...data.medications.map((m) => medToDraft(m, photo)), ...data.providers.map((p) => providerToDraft(p, photo)));
        } catch (e) {
          if (firstError === null) firstError = e;
          out.failed.push(photo);
        }
      }
      if (out.failed.length === files.length) throw firstError;
      return out;
    },
    onMutate: () => {
      setExtractErrorKey(null);
      setPhase('reading');
    },
    onSuccess: (data) => {
      setRows(data.rows);
      setFoundRows(data.rows.length > 0);
      setFailedPhotos(data.failed);
      setPhotoTotal(data.total);
      setReadingPhoto(null);
      setPhase('review');
    },
    onError: (e) => {
      // An unreadable file is the "take a clearer photo" fix; a network
      // failure is the service not answering; every failure shows a notice.
      setExtractErrorKey(photoIntakeKeyOf(e));
      setReadingPhoto(null);
      setPhase('pick');
    },
  });

  function onPick(e: React.ChangeEvent<HTMLInputElement>): void {
    const files = Array.from(e.target.files ?? []);
    // Reset the input so re-picking the same files fires change again.
    e.target.value = '';
    if (files.length > 0) extractMut.mutate(files);
  }

  function patchRow(id: string, patch: Partial<DraftRow>): void {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }

  /** Dismiss removes the card from review entirely: a dismissed entry was
   *  never added, so it must not read as added. */
  function dismissRow(id: string): void {
    setRows((prev) => prev.filter((r) => r.id !== id));
  }

  /** One card's add, through the record-add path; the card shows its own
   *  result. The row as it stands at the press is what travels. */
  async function addRow(row: DraftRow): Promise<void> {
    patchRow(row.id, { status: 'adding', error: null });
    try {
      await postRecordRow(tenantSlug, clientId, PROJECTION[row.kind], addFields(row));
      await queryClient.invalidateQueries({ queryKey: ['clientRecord'] });
      patchRow(row.id, { status: 'added', adjusting: false, searching: false, error: null });
    } catch (err) {
      patchRow(row.id, { status: 'pending', error: recordEditErrorMessage(err, errorContextFor(row), contact) });
    }
  }

  /** "Update {X}": the photo's dose and directions onto that row on the
   *  record, through the record form's edit. */
  async function updateRow(row: DraftRow, target: PhotoRecordMedication): Promise<void> {
    patchRow(row.id, { status: 'adding', error: null });
    const onRecord = cachedClientRecord(queryClient, tenantSlug, clientId)?.medications.find((m) => m.id === target.id);
    try {
      await patchRecordRow(tenantSlug, clientId, 'medications', target.id, {
        action: 'edit',
        fields: updateFields(row, onRecord?.prescriber),
      });
      await queryClient.invalidateQueries({ queryKey: ['clientRecord'] });
      patchRow(row.id, { status: 'added', outcome: 'updated', adjusting: false, searching: false, error: null });
    } catch (err) {
      patchRow(row.id, { status: 'pending', error: recordEditErrorMessage(err, errorContextFor(row), contact) });
    }
  }

  /** Every ticked ready card, one at a time, in list order. */
  async function addAll(ready: DraftRow[]): Promise<void> {
    for (const row of ready) await addRow(row);
  }

  const ordered = [...rows.filter((r) => r.kind === 'medication'), ...rows.filter((r) => r.kind === 'provider')];
  const ready = ordered.filter(inBulk);
  const needsName = ordered.filter((r) => r.status === 'pending' && nameOf(r) === '').length;
  const pending = rows.filter((r) => r.status !== 'added');
  const addedCount = rows.filter((r) => r.status === 'added' && r.outcome === 'added').length;
  const updatedCount = rows.filter((r) => r.status === 'added' && r.outcome === 'updated').length;
  const careTeam = cachedClientRecord(queryClient, tenantSlug, clientId);

  // "Add {n} to the record" sits beside Done too, so a long review ends
  // where the person finishes reading.
  const footer =
    phase === 'review' ? (
      <div className="flex flex-wrap items-center justify-end gap-2">
        {ready.length > 0 && (
          <Button variant="secondary" size="sm" data-testid="photo-intake-add-all-footer" onClick={() => void addAll(ready)}>
            {`Add ${ready.length} to the record`}
          </Button>
        )}
        <Button variant="primary" size="sm" onClick={onClose} data-testid="photo-intake-done">
          Done{doneCount(addedCount, updatedCount)}
        </Button>
      </div>
    ) : (
      <Button variant="ghost" size="sm" onClick={onClose}>
        Cancel
      </Button>
    );

  return (
    <Dialog title="Add from a photo" size="xl" testId="photo-intake-dialog" onClose={onClose} footer={footer}>
      {/* Hidden file input; no `capture`, so a phone offers the camera and
          the photo library both (LP-27). */}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        data-testid="photo-intake-file"
        onChange={onPick}
      />

      {phase === 'pick' && (
        <div className="flex flex-col items-center gap-4 py-6 text-center">
          <p className="max-w-md text-sm text-ink-soft">
            Take a photo of a medication list, a pill bottle, a page of providers, or a business
            card. We’ll read it and show you each entry to add. Nothing is added until you say so, and the
            photo is not kept.
          </p>
          <Button
            variant="primary"
            icon={<Camera size={16} aria-hidden="true" />}
            data-testid="photo-intake-choose"
            onClick={() => fileRef.current?.click()}
          >
            Choose photos
          </Button>
          {extractErrorKey !== null && (
            <ErrorNotice surface="photo_intake_extract" code={extractErrorKey} data-testid="photo-intake-error" />
          )}
        </div>
      )}

      {phase === 'reading' && (
        <div className="py-6">
          <ReadingIndicator kind="photo" progress={readingPhoto} testId="photo-intake-reading" />
        </div>
      )}

      {phase === 'review' && (
        <div className="flex flex-col gap-4 py-1">
          {failedPhotos.length > 0 && (
            <ul className="flex flex-col gap-1" data-testid="photo-intake-failed">
              {failedPhotos.map((n) => (
                <li key={n} className="text-sm text-pepper" data-testid={`photo-intake-failed-${n}`}>
                  {photoFailedLine(n)}
                </li>
              ))}
            </ul>
          )}
          {!foundRows ? (
            <div className="flex flex-col items-center gap-3 py-4 text-center">
              <p className="text-sm text-ink-soft">
                {photoTotal > 1
                  ? 'We couldn’t find a medication or a provider in those photos.'
                  : 'We couldn’t find a medication or a provider in that photo.'}
              </p>
              <Button variant="secondary" size="sm" onClick={() => setPhase('pick')} data-testid="photo-intake-retry">
                Try another photo
              </Button>
            </div>
          ) : (
            <>
              <p className="text-sm text-ink-soft">
                Here is what we read. Add what is right, adjust what is not, dismiss the rest.
                Nothing is added until you say so.
              </p>
              {(ready.length > 0 || needsName > 0) && (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  {ready.length > 0 && (
                    <Button
                      variant="primary"
                      size="sm"
                      data-testid="photo-intake-add-all"
                      onClick={() => void addAll(ready)}
                    >
                      {`Add ${ready.length} to the record`}
                    </Button>
                  )}
                  {needsName > 0 && (
                    <span data-testid="photo-intake-needs-name" className="text-sm text-ink-soft">
                      {needsName === 1 ? '1 needs a name' : `${needsName} need a name`}
                    </span>
                  )}
                </div>
              )}
              {(['medication', 'provider'] as const).map((kind) => {
                const sectionRows = ordered.filter((r) => r.kind === kind);
                if (sectionRows.length === 0) return null;
                return (
                  <section key={kind} data-testid={`photo-intake-section-${kind}`}>
                    <h3 className="mb-2 font-display text-lg text-sage-800">
                      {kind === 'medication' ? 'Medications' : 'Doctors & providers'}
                    </h3>
                    <ul className="flex flex-col gap-2">
                      {sectionRows.map((row) => (
                        <ReviewCard
                          key={row.id}
                          row={row}
                          photoMark={photoTotal > 1 ? row.photo : null}
                          careTeam={careTeam?.providers ?? []}
                          pronouns={careTeam?.vitals.pronouns ?? null}
                          onPatch={patchRow}
                          onAdd={(r) => void addRow(r)}
                          onUpdate={(r, target) => void updateRow(r, target)}
                          onDismiss={dismissRow}
                        />
                      ))}
                    </ul>
                  </section>
                );
              })}
              {pending.length === 0 && (
                <p className="text-center text-sm font-medium text-sage-800" data-testid="photo-intake-all-done">
                  All set. {allSetLine(addedCount, updatedCount)}
                </p>
              )}
            </>
          )}
        </div>
      )}
    </Dialog>
  );
}

// ---------------------------------------------------------------------
// One card
// ---------------------------------------------------------------------

/** The Done button's count: "(2 added)", "(1 updated)", "(2 added, 1 updated)". */
function doneCount(added: number, updated: number): string {
  const parts = [...(added > 0 ? [`${added} added`] : []), ...(updated > 0 ? [`${updated} updated`] : [])];
  return parts.length === 0 ? '' : ` (${parts.join(', ')})`;
}

function allSetLine(added: number, updated: number): string {
  if (added === 0 && updated === 0) return 'Nothing left to review.';
  const parts = [...(added > 0 ? [`${added} added to the record`] : []), ...(updated > 0 ? [`${updated} updated`] : [])];
  return `${parts.join(', ')}.`;
}

function ReviewCard({
  row,
  photoMark,
  careTeam,
  pronouns,
  onPatch,
  onAdd,
  onUpdate,
  onDismiss,
}: {
  row: DraftRow;
  /** Which photo the row came from, shown when more than one was read. */
  photoMark: number | null;
  careTeam: Parameters<typeof CareTeamCombobox>[0]['careTeam'];
  pronouns: Parameters<typeof CareTeamCombobox>[0]['pronouns'];
  onPatch: (id: string, patch: Partial<DraftRow>) => void;
  onAdd: (row: DraftRow) => void;
  onUpdate: (row: DraftRow, target: PhotoRecordMedication) => void;
  onDismiss: (id: string) => void;
}): ReactNode {
  const name = nameOf(row);

  if (row.status === 'added') {
    return (
      <li
        data-testid="photo-intake-row"
        data-status="confirmed"
        className="flex items-center gap-2 rounded-base border border-sage-800/[6%] bg-sage-50 px-3 py-2 opacity-80"
      >
        <Check size={16} className="shrink-0 text-sage-700" aria-hidden="true" />
        <span className="text-sm font-semibold text-sage-800">{name || 'Entry'}</span>
        <span className="text-xs text-ink-soft">{row.outcome}</span>
      </li>
    );
  }

  const setField = (key: string, value: string): void => onPatch(row.id, { fields: { ...row.fields, [key]: value }, error: null });
  const facts = factsOf(row);

  return (
    <li data-testid="photo-intake-row" data-status="pending" className="rounded-base border border-sage-800/[8%] px-3 py-2.5">
      <div className="flex items-start justify-between gap-3">
        <span className="flex flex-wrap items-center gap-2">
          <span className="rounded-pill bg-sage-50 px-2 py-0.5 text-label font-semibold uppercase tracking-wide text-sage-700">
            {CHIP[row.kind]}
          </span>
          {photoMark !== null && (
            <span className="text-xs text-ink-soft" data-testid="photo-intake-row-photo">
              Photo {photoMark}
            </span>
          )}
          {row.match !== null && row.match.state !== 'near' && name !== '' && (
            <span className="text-xs font-semibold text-sage-800" data-testid="photo-intake-row-state">
              {row.match.state === 'on_record' ? 'Already on the record' : 'New'}
            </span>
          )}
        </span>
        <Button
          variant="ghost"
          size="sm"
          data-testid={row.adjusting ? 'photo-intake-adjust-done' : 'photo-intake-adjust'}
          onClick={() => onPatch(row.id, { adjusting: !row.adjusting })}
        >
          {row.adjusting ? 'Done' : 'Adjust'}
        </Button>
      </div>

      {!row.adjusting ? (
        facts.length > 0 && (
          <dl className="mt-1 grid gap-1">
            {facts.map((f) => (
              <div key={f.key} className="flex flex-wrap items-baseline gap-x-3">
                <dt className="min-w-[9.5em] shrink-0 text-label font-semibold uppercase tracking-label text-sage-400">{f.label}</dt>
                <dd className="m-0 min-w-0 flex-1 basis-[9em] text-value font-medium text-sage-800 [overflow-wrap:anywhere]">{f.value}</dd>
              </div>
            ))}
          </dl>
        )
      ) : (
        <div className="mt-2 grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
          {keysFor(row.kind).map((key) => {
            const id = `${row.id}-${key}`;
            const label = labelFor(row.kind, key);
            const testId = `photo-intake-field-${key}`;
            const required = key === 'name' || key === 'display_name';
            if (key === 'times_of_day') {
              return (
                <div key={key} className="sm:col-span-2">
                  <TickGroup
                    id={id}
                    legend={label}
                    options={MEDICATION_TIMES_OF_DAY.map((t) => ({ value: t, label: WHEN_WORDS[t] ?? t }))}
                    value={row.times}
                    onChange={(next) => onPatch(row.id, { times: MEDICATION_TIMES_OF_DAY.filter((t) => next.includes(t)) })}
                    testIdPrefix={testId}
                  />
                </div>
              );
            }
            if (key === 'prescriber') {
              return (
                <Field key={key} label={label} htmlFor={id}>
                  <CareTeamCombobox
                    id={id}
                    value={row.fields[key] ?? ''}
                    pick={row.prescriberPick}
                    onChange={(next) => onPatch(row.id, { fields: { ...row.fields, [key]: next.value }, prescriberPick: next.pick, error: null })}
                    careTeam={careTeam}
                    pronouns={pronouns}
                    listLabel={PRESCRIBER.listLabel}
                    testIdPrefix={testId}
                  />
                </Field>
              );
            }
            if (key === 'office_address') {
              // The house address fields (0234); a photo read as one line
              // shows its words above them. A card runs no address check.
              const photoText = row.fields[PHOTO_TEXT_KEY];
              const part = (p: AddressPart): string => row.fields[partKey(p)] ?? '';
              const setPart = (p: AddressPart) => (v: string): void => setField(partKey(p), v);
              return (
                <fieldset key={key} data-testid={testId} className="flex min-w-0 flex-col gap-2 sm:col-span-2">
                  <legend className="text-xs text-ink-soft">{label}</legend>
                  {photoText !== undefined && (
                    <p data-testid="photo-intake-address-text" className="text-xs text-sage-800">
                      From the photo: {photoText}
                    </p>
                  )}
                  <AddressFields
                    idPrefix={`${id}-address`}
                    values={{ line1: part('line1'), line2: part('line2'), city: part('city'), state: part('state'), zip: part('zip') }}
                    onLine1Change={setPart('line1')}
                    onLine2Change={setPart('line2')}
                    onCityChange={setPart('city')}
                    onStateChange={setPart('state')}
                    onZipChange={setPart('zip')}
                  />
                </fieldset>
              );
            }
            return (
              <Field key={key} label={label} required={required} htmlFor={id}>
                <TextInput
                  id={id}
                  data-testid={testId}
                  type={key === 'office_phone' ? 'tel' : key === 'email' ? 'email' : 'text'}
                  className={FLD}
                  value={row.fields[key] ?? ''}
                  onChange={(e) => setField(key, key === 'office_phone' ? formatPhoneInput(e.target.value) : e.target.value)}
                  {...(key === 'office_phone' ? {} : textBoxProps(textKindFor(row.kind, key)))}
                />
              </Field>
            );
          })}
        </div>
      )}

      {row.kind === 'provider' && <DirectoryLinkLine row={row} onPatch={onPatch} />}

      {name === '' && (
        <p className="mt-1.5 text-sm text-pepper" data-testid="photo-intake-needs-name-line">
          Needs a name. Open Adjust and type it.
        </p>
      )}

      {row.match !== null && row.match.state !== 'near' && name !== '' && (
        <div className="mt-1" data-testid="photo-intake-row-match" data-state={row.match.state}>
          {/* The box says what it does: it puts the row in "Add {n} to the
              record". A row already on the record says "anyway". */}
          <CheckboxRow
            id={`${row.id}-tick`}
            testId="photo-intake-row-tick"
            label={row.match.state === 'on_record' ? `Add ${name} anyway` : `Add ${name}`}
            checked={row.ticked}
            disabled={row.status === 'adding'}
            onChange={(ticked) => onPatch(row.id, { ticked })}
          />
        </div>
      )}

      {row.match?.state === 'near' && name !== '' ? (
        <div className="mt-2 flex flex-col gap-2" data-testid="photo-intake-row-match" data-state="near">
          {row.match.rows.map((target) => (
            <p key={target.id} className="text-sm text-sage-800" data-testid="photo-intake-did-you-mean">
              Did you mean {medicationRowValue(target)}, already on the record?
            </p>
          ))}
          <div className="flex flex-wrap items-center gap-2">
            {row.match.rows.map((target) => (
              <Button
                key={target.id}
                variant="secondary"
                size="sm"
                data-testid={`photo-intake-row-update-${target.id}`}
                disabled={row.status === 'adding'}
                onClick={() => onUpdate(row, target)}
              >
                {`Update ${medicationRowValue({ name: target.name, dose: null })}`}
              </Button>
            ))}
            <Button
              variant="secondary"
              size="sm"
              data-testid="photo-intake-row-keep-both"
              disabled={row.status === 'adding'}
              loading={row.status === 'adding'}
              onClick={() => onAdd(row)}
            >
              Keep both
            </Button>
            <Button variant="ghost" size="sm" data-testid="photo-intake-row-dismiss" disabled={row.status === 'adding'} onClick={() => onDismiss(row.id)}>
              Dismiss
            </Button>
          </div>
        </div>
      ) : (
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {/* A checked row is added by its tick, through "Add {n} to the
            record"; a read with no check keeps its own Add. */}
        {name !== '' && row.match === null && (
          <Button
            variant="primary"
            size="sm"
            data-testid="photo-intake-row-add"
            disabled={row.status === 'adding'}
            loading={row.status === 'adding'}
            onClick={() => onAdd(row)}
          >
            {ADD_WORDS[row.kind]}
          </Button>
        )}
        <Button variant="ghost" size="sm" data-testid="photo-intake-row-dismiss" disabled={row.status === 'adding'} onClick={() => onDismiss(row.id)}>
          Dismiss
        </Button>
      </div>
      )}

      {row.error !== null && (
        <p role="alert" data-testid="photo-intake-row-error" className="mt-1.5 text-[12px] text-pepper">
          {row.error}
        </p>
      )}
    </li>
  );
}

/** A provider's Directory link: the search the confirm queue's provider
 *  card uses; a pick travels with the add as one Directory id. */
function DirectoryLinkLine({ row, onPatch }: { row: DraftRow; onPatch: (id: string, patch: Partial<DraftRow>) => void }): ReactNode {
  const BRINGS = 'Linking brings their phone, email and address from the Directory.';
  if (row.directoryLink !== null) {
    return (
      <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-sage-800">
        <span data-testid="photo-intake-linked">Linked to {row.directoryLink.name}.</span>
        <span className="text-xs text-ink-soft">{BRINGS}</span>
        <Button variant="ghost" size="sm" onClick={() => onPatch(row.id, { directoryLink: null })}>
          Unlink
        </Button>
      </div>
    );
  }
  if (!row.searching) {
    return (
      <div className="mt-2">
        <Button variant="ghost" size="sm" data-testid="photo-intake-link-directory" onClick={() => onPatch(row.id, { searching: true })}>
          Link a Directory profile
        </Button>
      </div>
    );
  }
  return (
    <div className="mt-2">
      <p className="mb-1 text-xs text-ink-soft">{BRINGS}</p>
      <DirectorySearch
        onPick={(pick) =>
          onPatch(row.id, {
            searching: false,
            directoryLink: { kind: pick.type === 'organization' ? 'org' : 'individual', id: pick.id, name: pick.display_name },
          })
        }
        onEscapeEmpty={() => onPatch(row.id, { searching: false })}
      />
    </div>
  );
}
